import * as Icons from "lucide-react";
import { VALUES, STATS } from "../lib/constants";
import { CountUp } from "../lib/hooks";

const ABOUT_IMG =
  "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="sobre" data-testid="about-section" className="py-20 md:py-32 bg-[#F5F7FA] relative overflow-hidden">
      <div className="absolute top-20 -left-20 w-72 h-72 bg-[#00C7A5]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-20 w-96 h-96 bg-[#2F5887]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="reveal relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-[#00C7A5] rounded-2xl" aria-hidden="true" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#2F5887] rounded-2xl" aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img src={ABOUT_IMG} alt="Equipe Master Business" className="w-full h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2F5887]/40 via-transparent to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 lg:left-8 lg:translate-x-0 bg-white rounded-2xl shadow-2xl p-5 flex items-center gap-4 min-w-[260px]">
              <div className="w-14 h-14 rounded-xl bg-[#00C7A5]/10 flex items-center justify-center">
                <Icons.Award className="w-7 h-7 text-[#00C7A5]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111111] font-display">+8 Anos</p>
                <p className="text-sm text-[#4B5563]">de atuação</p>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="reveal">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#00C7A5] mb-4">
              Sobre Nós
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2F5887] leading-tight mb-6">
              Experiência e compromisso a serviço do seu negócio
            </h2>
            <p className="text-[#4B5563] text-lg leading-relaxed mb-5">
              Fundada em junho de 2017, a <strong className="text-[#111111]">Master Business Consultoria Contábil</strong> é um escritório especializado em serviços contábeis sediado no Setor Sul, em Goiânia-GO. Ao longo de mais de 8 anos de atuação, construímos uma trajetória sólida ao lado de empresas dos segmentos <strong className="text-[#111111]">Comercial, de Serviços, Industrial e do Terceiro Setor</strong>.
            </p>
            <p className="text-[#4B5563] text-base leading-relaxed mb-5">
              Nossa equipe técnica alia rigor contábil, atualização constante da legislação e um atendimento próximo e personalizado. Acreditamos que uma boa contabilidade vai além do cumprimento de obrigações — ela é uma ferramenta estratégica para o crescimento sustentável do seu negócio.
            </p>
            <p className="text-[#4B5563] text-base leading-relaxed mb-8">
              Além da contabilidade empresarial tradicional, somos referência em <strong className="text-[#111111]">Perícia Contábil Judicial</strong> — com registro no Banco de Peritos do TJGO —, <strong className="text-[#111111]">Contabilidade Pública</strong> e <strong className="text-[#111111]">Contabilidade Eleitoral</strong> para partidos e candidatos, áreas que demandam especialização técnica aprofundada e pleno domínio da legislação vigente.
            </p>

            <button
              data-testid="about-cta-button"
              onClick={() => scrollTo("servicos")}
              className="inline-flex items-center gap-2 bg-[#2F5887] hover:bg-[#1C3B61] text-white font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-md"
            >
              Conheça nossos serviços
              <Icons.ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mission / Vision / Values */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-24 lg:mt-32">
          {VALUES.map((v, i) => {
            const Icon = Icons[v.icon] || Icons.Star;
            return (
              <div
                key={i}
                data-testid={`value-card-${i}`}
                className="reveal bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 card-glow"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#2F5887] to-[#00C7A5] flex items-center justify-center mb-6">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#111111] mb-3">{v.title}</h3>
                <p className="text-[#4B5563] leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="mt-20 lg:mt-28 bg-gradient-to-br from-[#2F5887] to-[#1C3B61] rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#00C7A5]/20 rounded-full blur-3xl" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 relative">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center text-white">
                <div className="text-5xl lg:text-6xl font-bold text-[#00C7A5] mb-2">
                  <CountUp to={s.value} suffix={s.suffix} />
                </div>
                <p className="text-white/90 font-medium text-sm uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
